import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { AlertTriangle, CheckCircle, Clock, Search, X, Plus, Zap, IndianRupee, Trash2, Send, Download, Loader2, RefreshCw, Pause, Play, Pencil } from 'lucide-react'
import { getInvoices, createInvoice as createInvoiceSvc, updateInvoice as updateInvoiceSvc, deleteInvoice as deleteInvoiceSvc } from '../services/invoices.service'
import { getClients } from '../services/clients.service'
import { supabase } from '../lib/supabase'
import type { Client } from '../types'
import { useToast } from '../context/ToastContext'
import { useActivity } from '../context/ActivityContext'
import { useIsAdmin } from '../hooks/useIsAdmin'
import { useDateRange } from '../context/DateRangeContext'
import type { Invoice, InvoiceItem } from '../types'

const API = '/api'

// ── helpers ────────────────────────────────────────────────────
function getNextBillingDate(day: number): string {
  const today = new Date()
  const d = new Date(today.getFullYear(), today.getMonth(), day)
  if (d <= today) d.setMonth(d.getMonth() + 1)
  return d.toISOString().split('T')[0]
}

function ordinal(n: number) {
  const s = ['th','st','nd','rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

// ── Recurring Setup Modal ──────────────────────────────────────
interface RecurringSetup {
  id?: string
  client_id: string
  client_name: string
  billing_day: number
  gst_enabled: boolean
  gst_rate: number
  status: 'active' | 'paused'
  next_billing_date?: string
  last_billed_date?: string
  ad_spend?: number
  ad_spend_note?: string
  misc?: number
  misc_note?: string
}

function RecurringModal({
  clients, existing, onClose, onSave,
}: {
  clients: Client[]
  existing?: RecurringSetup
  onClose: () => void
  onSave: () => void
}) {
  const { toast } = useToast()
  const activeClients = clients.filter(c => c.status === 'active')
  const [clientId,    setClientId]    = useState(existing?.client_id    ?? activeClients[0]?.id ?? '')
  const [billingDay,  setBillingDay]  = useState(existing?.billing_day  ?? 1)
  const [gstEnabled,  setGstEnabled]  = useState(existing?.gst_enabled  ?? true)
  const [gstRate,     setGstRate]     = useState(String(existing?.gst_rate ?? 18))
  const [adSpend,     setAdSpend]     = useState(String(existing?.ad_spend || ''))
  const [adSpendNote, setAdSpendNote] = useState(existing?.ad_spend_note ?? '')
  const [misc,        setMisc]        = useState(String(existing?.misc || ''))
  const [miscNote,    setMiscNote]    = useState(existing?.misc_note ?? '')
  const [saving,      setSaving]      = useState(false)

  const client      = clients.find(c => c.id === clientId)
  const mrr         = client?.mrr ?? 0
  const adSpendNum  = parseFloat(adSpend)  || 0
  const miscNum     = parseFloat(misc)     || 0
  const gstPct      = gstEnabled ? (parseFloat(gstRate) || 0) / 100 : 0
  const tax         = Math.round(mrr * gstPct)          // GST on retainer only
  const serviceTotal = mrr + tax
  const grandTotal   = serviceTotal + adSpendNum + miscNum

  const inputCls = 'w-full rounded-lg px-3 py-2 text-sm text-white outline-none'
  const iSty     = { background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)' }
  const lSty     = { color: '#a0a0a0' }

  const handleSave = async () => {
    if (!clientId) { toast('Select a client', 'error'); return }
    setSaving(true)
    try {
      const fullPayload = {
        client_id:         clientId,
        client_name:       client?.name ?? '',
        billing_day:       billingDay,
        gst_enabled:       gstEnabled,
        gst_rate:          parseFloat(gstRate) || 0,
        status:            existing?.status ?? 'active',
        next_billing_date: getNextBillingDate(billingDay),
        ad_spend:          adSpendNum,
        ad_spend_note:     adSpendNote.trim(),
        misc:              miscNum,
        misc_note:         miscNote.trim(),
      }
      let saveError: any = null
      if (existing?.id) {
        const { error } = await supabase.from('recurring_invoices').update(fullPayload).eq('id', existing.id)
        saveError = error
      } else {
        const { error } = await supabase.from('recurring_invoices').insert(fullPayload)
        saveError = error
      }
      // If migration hasn't been run yet, fall back to basic payload
      if (saveError?.message?.includes('ad_spend') || saveError?.message?.includes('column')) {
        const basicPayload = { client_id: clientId, client_name: client?.name ?? '', billing_day: billingDay, gst_enabled: gstEnabled, gst_rate: parseFloat(gstRate) || 0, status: existing?.status ?? 'active', next_billing_date: getNextBillingDate(billingDay) }
        const { error: e2 } = existing?.id
          ? await supabase.from('recurring_invoices').update(basicPayload).eq('id', existing.id)
          : await supabase.from('recurring_invoices').insert(basicPayload)
        if (e2) throw e2
        toast('Saved — run the DB migration in Supabase SQL Editor to enable expense fields', 'info')
      } else if (saveError) {
        throw saveError
      } else {
        toast(existing ? 'Recurring billing updated ✓' : `Auto-billing set up for ${client?.name} ✓`, 'success')
      }
      onSave(); onClose()
    } catch (e: any) {
      toast(e.message || 'Failed to save', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.88)' }}>
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white font-syne">{existing ? 'Edit Recurring Billing' : 'Set Up Auto-Billing'}</h2>
          <button onClick={onClose} style={{ color: '#555' }}><X size={18} /></button>
        </div>

        <div className="space-y-4">
          {/* Client */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={lSty}>CLIENT</label>
            <select value={clientId} onChange={e => setClientId(e.target.value)} className={inputCls} style={iSty}>
              {activeClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Client info preview */}
          {client && (
            <div className="rounded-xl p-3 space-y-2" style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.08)' }}>
              <p className="text-xs font-semibold" style={{ color: '#555' }}>FROM CLIENT PROFILE</p>
              <div className="flex flex-wrap gap-1.5">
                {(client.services || []).map(s => (
                  <span key={s} className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(255,198,0,0.1)', color: '#ffc600' }}>{s}</span>
                ))}
              </div>
              <p className="text-sm font-bold font-syne" style={{ color: '#ffc600' }}>
                Monthly Retainer: ₹{mrr.toLocaleString('en-IN')}
              </p>
            </div>
          )}

          {/* Billing day */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={lSty}>BILLING DAY (every month)</label>
            <div className="flex items-center gap-3">
              <input
                type="number" min={1} max={28} value={billingDay}
                onChange={e => setBillingDay(Math.min(28, Math.max(1, parseInt(e.target.value) || 1)))}
                className="w-24 rounded-lg px-3 py-2 text-sm text-white text-center outline-none"
                style={iSty}
              />
              <span className="text-sm" style={{ color: '#a0a0a0' }}>
                Every <span className="text-white font-semibold">{ordinal(billingDay)}</span> of the month
              </span>
            </div>
            <p className="text-xs mt-1.5" style={{ color: '#555' }}>
              Next: {new Date(getNextBillingDate(billingDay)).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>

          {/* ── Expenses section ── */}
          <div className="rounded-xl p-4 space-y-3" style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.1)' }}>
            <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#a0a0a0' }}>
              Expenses Borne on Behalf of Client
            </p>
            <p className="text-xs" style={{ color: '#555' }}>Pass-through costs — no GST applied on these</p>

            {/* Ad Spend */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={lSty}>AD SPEND</label>
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: '#555' }}>₹</span>
                  <input type="number" min={0} placeholder="0" value={adSpend}
                    onChange={e => setAdSpend(e.target.value)}
                    className="w-full rounded-lg pl-7 pr-3 py-2 text-sm text-white outline-none"
                    style={iSty} />
                </div>
                <input type="text" placeholder="e.g. Google Ads + Meta Ads" value={adSpendNote}
                  onChange={e => setAdSpendNote(e.target.value)}
                  className={inputCls} style={iSty} />
              </div>
            </div>

            {/* Misc */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={lSty}>MISCELLANEOUS</label>
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: '#555' }}>₹</span>
                  <input type="number" min={0} placeholder="0" value={misc}
                    onChange={e => setMisc(e.target.value)}
                    className="w-full rounded-lg pl-7 pr-3 py-2 text-sm text-white outline-none"
                    style={iSty} />
                </div>
                <input type="text" placeholder="e.g. Domain, printing" value={miscNote}
                  onChange={e => setMiscNote(e.target.value)}
                  className={inputCls} style={iSty} />
              </div>
            </div>
          </div>

          {/* GST toggle */}
          <div className="flex items-center justify-between px-3 py-2.5 rounded-lg" style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.1)' }}>
            <div className="flex items-center gap-2.5">
              <button type="button" onClick={() => setGstEnabled(v => !v)}
                className="relative w-9 h-5 rounded-full transition-colors flex-shrink-0"
                style={{ background: gstEnabled ? '#ffc600' : '#333' }}>
                <span className="absolute top-0.5 rounded-full w-4 h-4 bg-white transition-all"
                  style={{ left: gstEnabled ? '18px' : '2px' }} />
              </button>
              <div>
                <span className="text-sm font-medium text-white">GST</span>
                <span className="text-xs ml-1.5" style={{ color: '#555' }}>(on retainer only)</span>
              </div>
            </div>
            {gstEnabled ? (
              <div className="flex items-center gap-1.5">
                <input type="number" value={gstRate} onChange={e => setGstRate(e.target.value)}
                  min="0" max="100"
                  className="w-14 rounded-lg px-2 py-1 text-sm text-white text-center outline-none"
                  style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.3)' }} />
                <span className="text-sm" style={{ color: '#a0a0a0' }}>%</span>
              </div>
            ) : (
              <span className="text-xs" style={{ color: '#555' }}>No GST</span>
            )}
          </div>

          {/* Invoice preview */}
          {client && (
            <div className="rounded-xl p-4 space-y-1.5" style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.1)' }}>
              <p className="text-xs font-semibold mb-2" style={{ color: '#555' }}>INVOICE PREVIEW</p>

              <div className="flex justify-between text-sm" style={{ color: '#a0a0a0' }}>
                <span>Monthly Retainer</span>
                <span className="text-white">₹{mrr.toLocaleString('en-IN')}</span>
              </div>
              {gstEnabled && tax > 0 && (
                <div className="flex justify-between text-sm" style={{ color: '#a0a0a0' }}>
                  <span>GST ({gstRate}%)</span>
                  <span className="text-white">₹{tax.toLocaleString('en-IN')}</span>
                </div>
              )}
              {(adSpendNum > 0 || miscNum > 0) && (
                <div className="flex justify-between text-sm pt-1 border-t" style={{ borderColor: 'rgba(255,198,0,0.08)', color: '#555' }}>
                  <span>Service total</span>
                  <span style={{ color: '#a0a0a0' }}>₹{serviceTotal.toLocaleString('en-IN')}</span>
                </div>
              )}
              {adSpendNum > 0 && (
                <div className="flex justify-between text-sm" style={{ color: '#a0a0a0' }}>
                  <span>Ad Spend{adSpendNote ? <span className="ml-1 text-xs" style={{ color: '#555' }}>({adSpendNote})</span> : null}</span>
                  <span className="text-white">₹{adSpendNum.toLocaleString('en-IN')}</span>
                </div>
              )}
              {miscNum > 0 && (
                <div className="flex justify-between text-sm" style={{ color: '#a0a0a0' }}>
                  <span>Misc{miscNote ? <span className="ml-1 text-xs" style={{ color: '#555' }}>({miscNote})</span> : null}</span>
                  <span className="text-white">₹{miscNum.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between text-base font-bold border-t pt-2.5 mt-1" style={{ borderColor: 'rgba(255,198,0,0.2)' }}>
                <span className="text-white">Total per invoice</span>
                <span style={{ color: '#ffc600' }}>₹{grandTotal.toLocaleString('en-IN')}</span>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2"
              style={{ background: '#ffc600', color: '#000' }}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {saving ? 'Saving…' : existing ? 'Save Changes' : 'Activate Auto-Billing'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Recurring Tab ──────────────────────────────────────────────
function RecurringTab({ clients, isAdmin }: { clients: Client[]; isAdmin: boolean }) {
  const { toast } = useToast()
  const [setups,    setSetups]    = useState<RecurringSetup[]>([])
  const [loading,   setLoading]   = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editing,   setEditing]   = useState<RecurringSetup | undefined>(undefined)

  const load = async () => {
    setLoading(true)
    const { data } = await supabase.from('recurring_invoices').select('*').order('created_at', { ascending: false })
    setSetups((data as RecurringSetup[]) || [])
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const toggleStatus = async (s: RecurringSetup) => {
    const newStatus = s.status === 'active' ? 'paused' : 'active'
    await supabase.from('recurring_invoices').update({ status: newStatus }).eq('id', s.id)
    setSetups(prev => prev.map(x => x.id === s.id ? { ...x, status: newStatus } : x))
    toast(`${s.client_name} billing ${newStatus === 'active' ? 'resumed' : 'paused'}`, 'info')
  }

  const deleteSetup = async (s: RecurringSetup) => {
    await supabase.from('recurring_invoices').delete().eq('id', s.id)
    setSetups(prev => prev.filter(x => x.id !== s.id))
    toast(`${s.client_name} auto-billing removed`, 'info')
  }

  const updateBillingDay = async (s: RecurringSetup, day: number) => {
    const next = getNextBillingDate(day)
    await supabase.from('recurring_invoices').update({ billing_day: day, next_billing_date: next }).eq('id', s.id)
    setSetups(prev => prev.map(x => x.id === s.id ? { ...x, billing_day: day, next_billing_date: next } : x))
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-sm" style={{ color: '#a0a0a0' }}>Invoices auto-generated & emailed on the set date every month</p>
        </div>
        {isAdmin && (
          <button onClick={() => { setEditing(undefined); setShowModal(true) }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: '#ffc600', color: '#000' }}>
            <Plus size={14} /> Add Client
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin" style={{ color: '#ffc600' }} />
        </div>
      ) : setups.length === 0 ? (
        <div className="text-center py-16" style={{ color: '#555' }}>
          <RefreshCw size={32} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No recurring billing set up yet.</p>
          <p className="text-xs mt-1">Add a client to start automating monthly invoices.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {setups.map(s => {
            const client    = clients.find(c => c.id === s.client_id)
            const mrr       = client?.mrr ?? 0
            const adSpend   = s.ad_spend  ?? 0
            const misc      = s.misc      ?? 0
            const gstPct    = s.gst_enabled ? (s.gst_rate || 18) / 100 : 0
            const tax       = Math.round(mrr * gstPct)
            const total     = mrr + tax + adSpend + misc
            const isActive  = s.status === 'active'
            const hasExpenses = adSpend > 0 || misc > 0

            return (
              <div key={s.id} className="rounded-xl p-4" style={{ background: '#0f0f0f', border: `1px solid ${isActive ? 'rgba(255,198,0,0.2)' : 'rgba(85,85,85,0.2)'}` }}>
                <div className="flex items-start justify-between gap-4">
                  {/* Left */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-bold text-white font-syne">{s.client_name}</p>
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={isActive
                          ? { background: 'rgba(255,198,0,0.12)', color: '#ffc600' }
                          : { background: 'rgba(85,85,85,0.2)', color: '#555' }}>
                        {isActive ? 'Active' : 'Paused'}
                      </span>
                    </div>

                    {/* Services */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {(client?.services || []).map(sv => (
                        <span key={sv} className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,198,0,0.06)', color: '#a0a0a0' }}>{sv}</span>
                      ))}
                    </div>

                    {/* Billing day (inline editable) */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs" style={{ color: '#555' }}>Every</span>
                      <input
                        type="number" min={1} max={28}
                        defaultValue={s.billing_day}
                        onBlur={e => {
                          const v = Math.min(28, Math.max(1, parseInt(e.target.value) || 1))
                          if (v !== s.billing_day) updateBillingDay(s, v)
                          e.target.value = String(v)
                        }}
                        className="w-12 rounded px-1.5 py-0.5 text-xs text-white text-center outline-none"
                        style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)' }}
                      />
                      <span className="text-xs" style={{ color: '#555' }}>of the month</span>
                      <span className="text-xs" style={{ color: '#a0a0a0' }}>
                        · Next: {s.next_billing_date ? new Date(s.next_billing_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
                      </span>
                      {s.last_billed_date && (
                        <span className="text-xs" style={{ color: '#555' }}>
                          · Last: {new Date(s.last_billed_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
                        </span>
                      )}
                    </div>

                    {/* Expenses breakdown tags */}
                    {hasExpenses && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {adSpend > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(255,198,0,0.06)', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.1)' }}>
                            Ad Spend ₹{adSpend.toLocaleString('en-IN')}{s.ad_spend_note ? ` · ${s.ad_spend_note}` : ''}
                          </span>
                        )}
                        {misc > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(255,198,0,0.06)', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.1)' }}>
                            Misc ₹{misc.toLocaleString('en-IN')}{s.misc_note ? ` · ${s.misc_note}` : ''}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Right — amount + actions */}
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-lg font-bold font-syne" style={{ color: '#ffc600' }}>
                        ₹{total.toLocaleString('en-IN')}
                      </p>
                      <p className="text-xs" style={{ color: '#555' }}>
                        {hasExpenses
                          ? `₹${(mrr + tax).toLocaleString('en-IN')} svc + ₹${(adSpend + misc).toLocaleString('en-IN')} exp`
                          : s.gst_enabled ? `incl. GST ${s.gst_rate}%` : 'no GST'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {isAdmin && (
                        <button onClick={() => { setEditing(s); setShowModal(true) }}
                          className="p-1.5 rounded-lg"
                          style={{ color: '#a0a0a0', background: '#1a1a1a' }}
                          title="Edit">
                          <Pencil size={12} />
                        </button>
                      )}
                      <button onClick={() => toggleStatus(s)}
                        className="p-1.5 rounded-lg"
                        style={{ color: isActive ? '#ffc600' : '#32c864', background: '#1a1a1a' }}
                        title={isActive ? 'Pause' : 'Resume'}>
                        {isActive ? <Pause size={12} /> : <Play size={12} />}
                      </button>
                      {isAdmin && (
                        <button onClick={() => deleteSetup(s)}
                          className="p-1.5 rounded-lg"
                          style={{ color: '#cc3300', background: '#1a1a1a' }}
                          title="Delete">
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {showModal && (
        <RecurringModal
          clients={clients}
          existing={editing}
          onClose={() => { setShowModal(false); setEditing(undefined) }}
          onSave={load}
        />
      )}
    </div>
  )
}

// ── New Invoice Modal ──────────────────────────────────────────
function NewInvoiceModal({ clients, onClose, onAdd }: { clients: Client[]; onClose: () => void; onAdd: (inv: Omit<Invoice, 'id'>) => void }) {
  const today   = new Date().toISOString().split('T')[0]
  const dueIn30 = new Date(Date.now() + 30 * 864e5).toISOString().split('T')[0]
  const activeClients = clients.filter(c => c.status !== 'prospect')
  const [clientId, setClientId] = useState(activeClients[0]?.id ?? clients[0]?.id ?? '')
  const [status, setStatus]     = useState<Invoice['status']>('draft')
  const [issuedAt, setIssuedAt] = useState(today)
  const [dueDate, setDueDate]   = useState(dueIn30)
  const [items, setItems]       = useState<{ description: string; quantity: string; rate: string }[]>([
    { description: '', quantity: '1', rate: '' },
  ])
  const [error, setError]     = useState('')
  const [gstEnabled, setGstEnabled] = useState(true)
  const [gstRate, setGstRate]       = useState('18')

  const parsedItems: InvoiceItem[] = items.map(it => ({
    description: it.description,
    quantity: parseFloat(it.quantity) || 1,
    rate: parseFloat(it.rate.replace(/[^0-9.]/g, '')) || 0,
    amount: (parseFloat(it.quantity) || 1) * (parseFloat(it.rate.replace(/[^0-9.]/g, '')) || 0),
  }))
  const subtotal  = parsedItems.reduce((s, i) => s + i.amount, 0)
  const gstPct    = gstEnabled ? (parseFloat(gstRate) || 0) / 100 : 0
  const tax       = Math.round(subtotal * gstPct)
  const total     = subtotal + tax

  const addItem    = () => setItems(prev => [...prev, { description: '', quantity: '1', rate: '' }])
  const removeItem = (i: number) => setItems(prev => prev.filter((_, idx) => idx !== i))
  const updateItem = (i: number, k: string, v: string) =>
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, [k]: v } : it))

  // Stable invoice number for this modal instance (INV-YYYYMM-XXXX)
  const [invoiceNum] = useState(() => {
    const now  = new Date()
    const yy   = now.getFullYear()
    const mm   = String(now.getMonth() + 1).padStart(2, '0')
    const rand = String(Math.floor(1000 + Math.random() * 9000))
    return `INV-${yy}${mm}-${rand}`
  })

  function generateInvoiceNumber() { return invoiceNum }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!clientId) { setError('Select a client.'); return }
    if (parsedItems.every(i => !i.description.trim())) { setError('Add at least one line item.'); return }
    const client = clients.find(c => c.id === clientId)
    onAdd({
      invoiceNumber: generateInvoiceNumber(), clientId, clientName: client?.name ?? '',
      amount: subtotal, tax, total, status, issuedAt, dueDate,
      items: parsedItems.filter(i => i.description.trim()),
    })
    onClose()
  }

  const inputCls   = 'w-full rounded-lg px-3 py-2 text-sm text-white outline-none'
  const inputStyle = { background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)' }
  const labelStyle = { color: '#a0a0a0' }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.88)' }}>
      <div className="w-full max-w-lg rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>New Invoice</h2>
            <p className="text-xs font-mono mt-0.5" style={{ color: '#555' }}>{generateInvoiceNumber()}</p>
          </div>
          <button onClick={onClose} style={{ color: '#555' }}><X size={18} /></button>
        </div>

        {error && (
          <div className="text-xs px-3 py-2 rounded-lg mb-4" style={{ background: 'rgba(204,51,0,0.12)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.3)' }}>{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>CLIENT *</label>
              <select value={clientId} onChange={e => { setClientId(e.target.value); setError('') }}
                className={inputCls} style={inputStyle}>
                {clients.filter(c => c.status !== 'prospect').map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>STATUS</label>
              <select value={status} onChange={e => setStatus(e.target.value as Invoice['status'])}
                className={inputCls} style={inputStyle}>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>ISSUE DATE</label>
              <input type="date" value={issuedAt} onChange={e => setIssuedAt(e.target.value)}
                className={inputCls} style={{ ...inputStyle, colorScheme: 'dark' }} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>DUE DATE</label>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                className={inputCls} style={{ ...inputStyle, colorScheme: 'dark' }} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold" style={labelStyle}>LINE ITEMS</label>
              <div className="flex items-center gap-1.5">
                <button type="button"
                  onClick={() => setItems(prev => [...prev, { description: 'Ad Spend', quantity: '1', rate: '' }])}
                  className="text-xs px-2 py-1 rounded"
                  style={{ color: '#a0a0a0', border: '1px solid rgba(255,255,255,0.1)' }}>
                  + Ad Spend
                </button>
                <button type="button"
                  onClick={() => setItems(prev => [...prev, { description: 'Misc Charges', quantity: '1', rate: '' }])}
                  className="text-xs px-2 py-1 rounded"
                  style={{ color: '#a0a0a0', border: '1px solid rgba(255,255,255,0.1)' }}>
                  + Misc
                </button>
                <button type="button" onClick={addItem}
                  className="flex items-center gap-1 text-xs px-2 py-1 rounded"
                  style={{ color: '#ffc600', border: '1px solid rgba(255,198,0,0.25)' }}>
                  <Plus size={11} /> Add Line
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid gap-2" style={{ gridTemplateColumns: '1fr 60px 90px 24px' }}>
                  <input value={it.description} onChange={e => updateItem(i, 'description', e.target.value)}
                    placeholder="Description" className={inputCls} style={inputStyle}
                    onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
                    onBlur={e => (e.target.style.borderColor  = 'rgba(255,198,0,0.2)')} />
                  <input value={it.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)}
                    placeholder="Qty" className={inputCls} style={{ ...inputStyle, textAlign: 'center' }} />
                  <input value={it.rate} onChange={e => updateItem(i, 'rate', e.target.value)}
                    placeholder="₹ Rate" className={inputCls} style={inputStyle} />
                  {items.length > 1 && (
                    <button type="button" onClick={() => removeItem(i)}
                      className="flex items-center justify-center rounded" style={{ color: '#555' }}>
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* GST toggle */}
          <div className="flex items-center justify-between px-3 py-2.5 rounded-lg" style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.1)' }}>
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => setGstEnabled(v => !v)}
                className="relative w-9 h-5 rounded-full transition-colors flex-shrink-0"
                style={{ background: gstEnabled ? '#ffc600' : '#333' }}>
                <span className="absolute top-0.5 rounded-full w-4 h-4 bg-white transition-all"
                  style={{ left: gstEnabled ? '18px' : '2px' }} />
              </button>
              <span className="text-sm font-medium text-white">GST</span>
            </div>
            {gstEnabled ? (
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  value={gstRate}
                  onChange={e => setGstRate(e.target.value)}
                  min="0" max="100"
                  className="w-14 rounded-lg px-2 py-1 text-sm text-white text-center outline-none"
                  style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.3)' }}
                />
                <span className="text-sm" style={{ color: '#a0a0a0' }}>%</span>
              </div>
            ) : (
              <span className="text-xs" style={{ color: '#555' }}>No GST</span>
            )}
          </div>

          {/* Totals */}
          <div className="rounded-xl p-4" style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.1)' }}>
            <div className="flex justify-between text-sm mb-1.5" style={{ color: '#a0a0a0' }}>
              <span>Subtotal</span><span className="text-white">₹{subtotal.toLocaleString('en-IN')}</span>
            </div>
            {gstEnabled && (
              <div className="flex justify-between text-sm mb-2" style={{ color: '#a0a0a0' }}>
                <span>GST ({gstRate}%)</span><span className="text-white">₹{tax.toLocaleString('en-IN')}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-bold border-t pt-2" style={{ borderColor: 'rgba(255,198,0,0.15)' }}>
              <span className="text-white">Total</span>
              <span style={{ color: '#ffc600' }}>₹{total.toLocaleString('en-IN')}</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
              Cancel
            </button>
            <button type="submit"
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#ffc600', color: '#000' }}>
              {status === 'draft' ? 'Save Draft' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Invoice Detail Modal ───────────────────────────────────────
function InvoiceDetailModal({
  invoice, clients, onClose, onMarkPaid, onMarkSent, onDelete, isAdmin,
}: {
  invoice: Invoice
  clients: Client[]
  onClose: () => void
  onMarkPaid: (id: string) => void
  onMarkSent: (id: string) => void
  onDelete: (id: string) => void
  isAdmin: boolean
}) {
  const { toast }         = useToast()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [sending,   setSending]   = useState(false)
  const [downloading, setDownloading] = useState(false)

  const s          = STATUS_STYLE[invoice.status]
  const canMarkPaid = invoice.status === 'sent' || invoice.status === 'overdue'
  const canSend    = invoice.status === 'draft' || invoice.status === 'sent' || invoice.status === 'overdue'
  const issued     = new Date(invoice.issuedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
  const due        = new Date(invoice.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })

  // Find client email
  const client     = clients.find(c => c.id === invoice.clientId)
  const clientEmail = client?.email || ''

  const handleSend = async () => {
    if (!clientEmail) {
      toast('No email on file for this client. Edit the client to add one.', 'error')
      return
    }
    setSending(true)
    try {
      const res  = await fetch(`${API}/send-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice, clientEmail, clientName: invoice.clientName }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Send failed')
      onMarkSent(invoice.id)
      toast(`Invoice sent to ${clientEmail} ✓`, 'success')
      onClose()
    } catch (err: any) {
      toast(err.message || 'Failed to send invoice', 'error')
    } finally {
      setSending(false)
    }
  }

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const res = await fetch(`${API}/download-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice }),
      })
      if (!res.ok) {
        const d = await res.json()
        throw new Error(d.error || 'Download failed')
      }
      const blob = await res.blob()
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = `${invoice.invoiceNumber}.pdf`
      a.click()
      URL.revokeObjectURL(url)
      toast(`${invoice.invoiceNumber}.pdf downloaded`, 'success')
    } catch (err: any) {
      toast(err.message || 'Failed to download PDF', 'error')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.88)' }}>
      <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.3)', maxHeight: '92vh', overflowY: 'auto' }}>

        {/* Invoice preview header */}
        <div className="px-6 pt-6 pb-4" style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.1)' }}>
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#ffc600' }}>
                <Zap size={15} color="#000" fill="#000" />
              </div>
              <div>
                <p className="text-sm font-bold text-white font-syne">Clickbait</p>
                <p className="text-xs" style={{ color: '#555' }}>Digital Marketing Agency</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-mono text-base font-bold" style={{ color: '#ffc600' }}>{invoice.invoiceNumber}</p>
              <span className="text-xs px-2 py-0.5 rounded mt-1 inline-block" style={{ background: s.bg, color: s.color }}>{s.label}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-xs font-semibold mb-1.5" style={{ color: '#555' }}>BILL TO</p>
              <p className="text-sm font-semibold text-white">{invoice.clientName}</p>
              {clientEmail && <p className="text-xs mt-0.5" style={{ color: '#555' }}>{clientEmail}</p>}
            </div>
            <div className="text-right">
              <div className="space-y-1">
                <div className="flex justify-end gap-4 text-xs">
                  <span style={{ color: '#555' }}>Issued</span>
                  <span style={{ color: '#a0a0a0' }}>{issued}</span>
                </div>
                <div className="flex justify-end gap-4 text-xs">
                  <span style={{ color: '#555' }}>Due</span>
                  <span style={{ color: invoice.status === 'overdue' ? '#cc3300' : '#a0a0a0' }}>{due}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="px-6 py-4">
          <table className="w-full text-sm mb-4">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,198,0,0.1)' }}>
                {['Description', 'Qty', 'Rate', 'Amount'].map(h => (
                  <th key={h} className="pb-2 text-xs font-semibold text-left" style={{ color: '#555' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(invoice.items ?? []).length > 0 ? (invoice.items ?? []).map((item, i) => (
                <tr key={i} style={{ borderBottom: '1px solid rgba(255,198,0,0.04)' }}>
                  <td className="py-2.5 text-white">{item.description}</td>
                  <td className="py-2.5 text-center" style={{ color: '#a0a0a0' }}>{item.quantity}</td>
                  <td className="py-2.5" style={{ color: '#a0a0a0' }}>₹{item.rate.toLocaleString('en-IN')}</td>
                  <td className="py-2.5 text-right font-medium text-white">₹{item.amount.toLocaleString('en-IN')}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={4} className="py-4 text-xs text-center" style={{ color: '#555' }}>
                    Retainer / service fee
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="rounded-xl p-4 space-y-2" style={{ background: '#1a1a1a' }}>
            <div className="flex justify-between text-sm">
              <span style={{ color: '#a0a0a0' }}>Subtotal</span>
              <span className="text-white">₹{invoice.amount.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: '#a0a0a0' }}>GST (18%)</span>
              <span className="text-white">₹{invoice.tax.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between text-base font-bold pt-2 border-t" style={{ borderColor: 'rgba(255,198,0,0.15)' }}>
              <span className="text-white">Total</span>
              <span className="flex items-center gap-1" style={{ color: '#ffc600' }}>
                <IndianRupee size={15} />{invoice.total.toLocaleString('en-IN')}
              </span>
            </div>
            {invoice.paidAt && (
              <div className="flex justify-between text-xs pt-1">
                <span style={{ color: '#555' }}>Paid on</span>
                <span style={{ color: '#32c864' }}>{new Date(invoice.paidAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-6 pb-6 space-y-2">

          {/* Download + Send row */}
          <div className="flex gap-2">
            <button onClick={handleDownload} disabled={downloading}
              className="flex items-center justify-center gap-1.5 flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all"
              style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.15)' }}>
              {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {downloading ? 'Generating…' : 'Download PDF'}
            </button>
            {canSend && isAdmin && (
              <button onClick={handleSend} disabled={sending}
                className="flex items-center justify-center gap-1.5 flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all"
                style={{ background: sending ? 'rgba(255,198,0,0.08)' : 'rgba(255,198,0,0.12)', color: '#ffc600', border: '1px solid rgba(255,198,0,0.3)' }}>
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {sending ? 'Sending…' : `Send to ${clientEmail ? clientEmail.split('@')[0] : 'Client'}`}
              </button>
            )}
          </div>

          {/* Mark paid / delete row */}
          {confirmDelete ? (
            <div className="flex gap-2">
              <button onClick={() => setConfirmDelete(false)}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                Cancel
              </button>
              {isAdmin && (
                <button onClick={() => { onDelete(invoice.id); onClose() }}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                  style={{ background: 'rgba(204,51,0,0.15)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.3)' }}>
                  Yes, Delete
                </button>
              )}
            </div>
          ) : (
            <div className="flex gap-2">
              {isAdmin && (
                <button onClick={() => setConfirmDelete(true)}
                  className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold flex-shrink-0"
                  style={{ background: '#1a1a1a', color: '#cc3300', border: '1px solid rgba(204,51,0,0.2)' }}>
                  <Trash2 size={14} />
                </button>
              )}
              <button onClick={onClose}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                Close
              </button>
              {canMarkPaid && isAdmin && (
                <button onClick={() => { onMarkPaid(invoice.id); onClose() }}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                  style={{ background: '#ffc600', color: '#000' }}>
                  Mark Paid ✓
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

type Filter = 'all' | Invoice['status']

function formatINR(n: number) { return '₹' + n.toLocaleString('en-IN') }

const STATUS_STYLE: Record<Invoice['status'], { bg: string; color: string; label: string }> = {
  paid:    { bg: 'rgba(232,160,0,0.15)',  color: '#e8a000', label: 'Paid' },
  sent:    { bg: 'rgba(255,198,0,0.12)',  color: '#ffc600', label: 'Sent' },
  overdue: { bg: 'rgba(204,51,0,0.15)',   color: '#cc3300', label: 'Overdue' },
  draft:   { bg: 'rgba(85,85,85,0.2)',    color: '#555555', label: 'Draft' },
}

export default function Invoices() {
  const [invoices,   setInvoices]   = useState<Invoice[]>([])
  const [clients,    setClients]    = useState<Client[]>([])
  const [filter,     setFilter]     = useState<Filter>('all')
  const [search,     setSearch]     = useState('')
  const [showNew,    setShowNew]    = useState(false)
  const [viewing,    setViewing]    = useState<Invoice | null>(null)
  const [activeTab,  setActiveTab]  = useState<'invoices' | 'recurring'>('invoices')
  const { toast }                   = useToast()
  const { addActivity }             = useActivity()
  const isAdmin                     = useIsAdmin()
  const { inRange }                 = useDateRange()
  const location                    = useLocation()

  useEffect(() => {
    Promise.all([getInvoices(), getClients()]).then(([invs, cls]) => {
      setInvoices(invs)
      setClients(cls)
      const s = location.state as any
      if (s?.openAdd) { setShowNew(true); window.history.replaceState({}, '') }
      else if (s?.viewInvoice) {
        const inv = invs.find(i => i.id === s.viewInvoice)
        if (inv) setViewing(inv)
        window.history.replaceState({}, '')
      }
    }).catch(console.error)
  }, [])

  const dateInvoices = invoices.filter(i => inRange(i.issuedAt))
  const filtered     = dateInvoices.filter(inv => {
    const matchStatus = filter === 'all' || inv.status === filter
    const matchSearch = search === '' ||
      inv.clientName.toLowerCase().includes(search.toLowerCase()) ||
      inv.invoiceNumber.toLowerCase().includes(search.toLowerCase())
    return matchStatus && matchSearch
  })

  const overdueInvoices = dateInvoices.filter(i => i.status === 'overdue')
  const overdueAmount   = overdueInvoices.reduce((s, i) => s + i.total, 0)
  const outstanding     = dateInvoices.filter(i => i.status === 'sent' || i.status === 'overdue').reduce((s, i) => s + i.total, 0)
  const paidThisMonth   = dateInvoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.total, 0)

  const markPaid = async (id: string) => {
    const inv = invoices.find(i => i.id === id)
    const updates = { status: 'paid' as const, paidAt: new Date().toISOString() }
    try {
      await updateInvoiceSvc(id, updates)
      setInvoices(prev => prev.map(i => i.id === id ? { ...i, ...updates } : i))
      if (inv) {
        toast(`${inv.invoiceNumber} marked as paid ✓`)
        addActivity({ type: 'invoice_paid', emoji: '💰', description: `Invoice paid: ${inv.invoiceNumber}`, detail: `${inv.clientName} · ₹${inv.total.toLocaleString('en-IN')}` })
      }
    } catch { toast('Update failed', 'error') }
  }

  const markSent = async (id: string) => {
    const inv = invoices.find(i => i.id === id)
    try {
      await updateInvoiceSvc(id, { status: 'sent' })
      setInvoices(prev => prev.map(i => i.id === id ? { ...i, status: 'sent' as const } : i))
      if (inv) addActivity({ type: 'invoice_sent', emoji: '📤', description: `Invoice sent: ${inv.invoiceNumber}`, detail: `${inv.clientName} · ₹${inv.total.toLocaleString('en-IN')}` })
    } catch { toast('Update failed', 'error') }
  }

  return (
    <div className="p-6 min-h-full" style={{ background: '#000' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl font-bold font-syne text-white">Invoices</h1>
        {activeTab === 'invoices' && isAdmin && (
          <button onClick={() => setShowNew(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: '#ffc600', color: '#000' }}>
            + New Invoice
          </button>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 mb-6 p-1 rounded-xl w-fit" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
        {(['invoices', 'recurring'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className="px-5 py-2 rounded-lg text-sm font-semibold capitalize transition-all"
            style={activeTab === tab
              ? { background: '#ffc600', color: '#000' }
              : { color: '#a0a0a0' }}>
            {tab === 'recurring' ? '⟳ Recurring' : 'Invoices'}
          </button>
        ))}
      </div>

      {activeTab === 'recurring' ? (
        <RecurringTab clients={clients} isAdmin={isAdmin} />
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
              <div className="flex items-center gap-2 mb-1"><Clock size={14} style={{ color: '#ffc600' }} /><p className="text-xs" style={{ color: '#a0a0a0' }}>Outstanding</p></div>
              <p className="text-xl font-bold font-syne text-white">{formatINR(outstanding)}</p>
            </div>
            <div className="rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(204,51,0,0.25)' }}>
              <div className="flex items-center gap-2 mb-1"><AlertTriangle size={14} style={{ color: '#cc3300' }} /><p className="text-xs" style={{ color: '#a0a0a0' }}>Overdue</p></div>
              <p className="text-xl font-bold font-syne" style={{ color: '#cc3300' }}>{formatINR(overdueAmount)}</p>
            </div>
            <div className="rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(232,160,0,0.2)' }}>
              <div className="flex items-center gap-2 mb-1"><CheckCircle size={14} style={{ color: '#e8a000' }} /><p className="text-xs" style={{ color: '#a0a0a0' }}>Paid This Month</p></div>
              <p className="text-xl font-bold font-syne" style={{ color: '#e8a000' }}>{formatINR(paidThisMonth)}</p>
            </div>
          </div>

          {overdueInvoices.length > 0 && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl mb-4" style={{ background: 'rgba(204,51,0,0.08)', border: '1px solid rgba(204,51,0,0.3)' }}>
              <AlertTriangle size={16} style={{ color: '#cc3300' }} />
              <p className="text-sm" style={{ color: '#cc3300' }}>
                <span className="font-semibold">{overdueInvoices.length} invoice{overdueInvoices.length > 1 ? 's' : ''} overdue</span>
                <span style={{ color: '#a0a0a0' }}> — {formatINR(overdueAmount)} pending collection</span>
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex gap-2">
              {(['all', 'draft', 'sent', 'paid', 'overdue'] as Filter[]).map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-all"
                  style={filter === f ? { background: '#ffc600', color: '#000' } : { background: '#0f0f0f', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                  {f}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg flex-1 max-w-xs" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
              <Search size={14} style={{ color: '#555' }} />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by client or #…"
                className="bg-transparent text-sm outline-none flex-1 placeholder-gray-600 text-white" />
            </div>
          </div>

          {/* Table */}
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,198,0,0.12)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.12)' }}>
                  {['Invoice #', 'Client', 'Amount', 'Tax', 'Total', 'Status', 'Issued', 'Due', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#a0a0a0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-12" style={{ color: '#555', background: '#0f0f0f' }}>No invoices found</td></tr>
                ) : filtered.map(inv => {
                  const s = STATUS_STYLE[inv.status]
                  return (
                    <tr key={inv.id}
                      style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.06)', borderLeft: inv.status === 'overdue' ? '2px solid #cc3300' : '2px solid transparent' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#1a1a1a')}
                      onMouseLeave={e => (e.currentTarget.style.background = '#0f0f0f')}>
                      <td className="px-4 py-3 font-mono text-xs" style={{ color: '#ffc600' }}>{inv.invoiceNumber}</td>
                      <td className="px-4 py-3 font-medium text-white">{inv.clientName}</td>
                      <td className="px-4 py-3" style={{ color: '#a0a0a0' }}>{formatINR(inv.amount)}</td>
                      <td className="px-4 py-3" style={{ color: '#a0a0a0' }}>{formatINR(inv.tax)}</td>
                      <td className="px-4 py-3 font-semibold text-white">{formatINR(inv.total)}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ background: s.bg, color: s.color }}>{s.label}</span>
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: '#a0a0a0' }}>{new Date(inv.issuedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}</td>
                      <td className="px-4 py-3 text-xs" style={{ color: inv.status === 'overdue' ? '#cc3300' : '#a0a0a0' }}>
                        {new Date(inv.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={() => setViewing(inv)}
                            className="text-xs px-2 py-1 rounded transition-colors"
                            style={{ color: '#ffc600', border: '1px solid rgba(255,198,0,0.25)' }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,198,0,0.08)')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                            View
                          </button>
                          {(inv.status === 'sent' || inv.status === 'overdue') && isAdmin && (
                            <button onClick={() => markPaid(inv.id)}
                              className="text-xs px-2 py-1 rounded"
                              style={{ background: 'rgba(232,160,0,0.15)', color: '#e8a000' }}>
                              Mark Paid
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showNew && (
        <NewInvoiceModal
          clients={clients}
          onClose={() => setShowNew(false)}
          onAdd={async inv => {
            try {
              const saved = await createInvoiceSvc(inv)
              setInvoices(prev => [saved, ...prev])
              toast(`${saved.invoiceNumber} created for ${saved.clientName}`)
              addActivity({ type: 'invoice_created', emoji: '🧾', description: `Invoice created: ${saved.invoiceNumber}`, detail: `${saved.clientName} · ₹${saved.total.toLocaleString('en-IN')}` })
            } catch { toast('Failed to create invoice', 'error') }
          }}
        />
      )}

      {viewing && (
        <InvoiceDetailModal
          invoice={viewing}
          clients={clients}
          isAdmin={isAdmin}
          onClose={() => setViewing(null)}
          onMarkPaid={id => { markPaid(id); setViewing(null) }}
          onMarkSent={id => { markSent(id); setViewing(null) }}
          onDelete={async id => {
            const inv = invoices.find(i => i.id === id)
            try {
              await deleteInvoiceSvc(id)
              setInvoices(prev => prev.filter(i => i.id !== id))
              setViewing(null)
              if (inv) {
                toast(`${inv.invoiceNumber} deleted`, 'info')
                addActivity({ type: 'invoice_deleted', emoji: '🗑️', description: `Invoice deleted: ${inv.invoiceNumber}`, detail: inv.clientName })
              }
            } catch { toast('Delete failed', 'error') }
          }}
        />
      )}
    </div>
  )
}
